import { useEffect, useState } from 'react';
import {
  Alternador,
  Aviso,
  Botao,
  Campo,
  CampoArea,
  Carregando,
  Cartao,
  Etiqueta,
  useDialogo,
} from '../../componentes/ui/index.ts';
import { api, ErroApi } from '../../lib/api.ts';
import type { Configuracao, RegrasEnvio } from '../../lib/tipos.ts';
import { FilaEmails } from './FilaEmails.tsx';
import { TrocarSenha } from './TrocarSenha.tsx';

const ROTULO_REGRA: Record<keyof RegrasEnvio, { titulo: string; texto: string }> = {
  confirmacao: {
    titulo: 'Confirmação de inscrição',
    texto: 'Enviada assim que o responsável envia a ficha.',
  },
  autorizacao: {
    titulo: 'Pedido de autorização',
    texto:
      'O texto que o responsável precisa responder autorizando a participação. Só conta quando ele responde — a confirmação em si é manual, pelo botão na ficha.',
  },
  lembretePagamento: {
    titulo: 'Lembrete de pagamento',
    texto: 'Para quem ainda tem saldo em aberto.',
  },
  avisoSaudeFaltando: {
    titulo: 'Aviso de saúde faltando',
    texto: 'Para fichas enviadas sem as informações de saúde.',
  },
  lembreteEmbarque: {
    titulo: 'Lembrete de embarque',
    texto: 'Detalhes finais, perto da data da viagem.',
  },
};

const DISPAROS = [
  { template: 'lembrete_pagamento', rotulo: 'Lembrete de pagamento' },
  { template: 'aviso_saude_faltando', rotulo: 'Aviso de saúde faltando' },
  { template: 'lembrete_embarque', rotulo: 'Lembrete de embarque' },
] as const;

export function Config() {
  const [config, setConfig] = useState<Configuracao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [disparando, setDisparando] = useState<string | null>(null);
  const { perguntar, dialogo } = useDialogo();

  useEffect(() => {
    api<{ config: Configuracao }>('/api/admin/config')
      .then((r) => setConfig(r.config))
      .catch((e: ErroApi) => setErro(e.mensagem))
      .finally(() => setCarregando(false));
  }, []);

  async function salvar(mudancas: Partial<Configuracao>) {
    setSalvando(true);
    setErro(null);
    setSucesso(null);
    try {
      const r = await api<{ config: Configuracao }>('/api/admin/config', {
        metodo: 'PATCH',
        corpo: mudancas,
      });
      setConfig(r.config);
      setSucesso('Configuração salva.');
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  async function disparar(template: string) {
    setDisparando(template);
    setErro(null);
    try {
      const r = await api<{ enfileirados: number; dias: number }>('/api/admin/disparos', {
        metodo: 'POST',
        corpo: { template },
      });
      setSucesso(
        r.enfileirados === 0
          ? 'Ninguém elegível para este aviso no momento.'
          : `${r.enfileirados} e-mails na fila, distribuídos em ${r.dias} ${
              r.dias === 1 ? 'dia' : 'dias'
            }.`,
      );
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro no disparo');
    } finally {
      setDisparando(null);
    }
  }

  if (carregando) return <Carregando />;
  if (!config) return <Aviso tipo="erro">{erro ?? 'Erro ao carregar configuração.'}</Aviso>;

  return (
    <div className="pilha-6">
      {dialogo}

      <div className="pilha-3">
        <Etiqueta>Configurações</Etiqueta>
        <h2>Formulário e avisos</h2>
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {sucesso && <Aviso tipo="sucesso">{sucesso}</Aviso>}

      <Cartao marcado titulo="Recebimento de inscrições">
        <div className="pilha">
          <div className="escolha" style={{ cursor: 'default' }}>
            <Alternador
              ligado={config.formOpen}
              desabilitado={salvando}
              aoMudar={(v) => void salvar({ formOpen: v })}
              titulo={config.formOpen ? 'Aberto' : 'Fechado'}
              descricao={
                config.formOpen
                  ? 'Qualquer responsável consegue enviar uma ficha.'
                  : 'Novos envios são recusados, inclusive de rascunhos antigos.'
              }
            />
          </div>

          <CampoArea
            rotulo="Mensagem quando fechado"
            value={config.closedMessage}
            onChange={(e) => setConfig({ ...config, closedMessage: e.target.value })}
            onBlur={() => void salvar({ closedMessage: config.closedMessage })}
          />

          {/* O terceiro estado: aceitar o atrasado sem reabrir para todo mundo. */}
          <Campo
            rotulo="Chave de exceção"
            value={config.chaveExcecao ?? ''}
            onChange={(e) => setConfig({ ...config, chaveExcecao: e.target.value })}
            onBlur={() => void salvar({ chaveExcecao: config.chaveExcecao || null })}
            opcional
            dica="Com o formulário fechado, quem tiver o link com ?chave=... ainda consegue enviar. Mínimo de 6 caracteres; em branco desliga."
          />

          <Campo
            rotulo="Valor do acampamento"
            value={config.valorPadrao}
            onChange={(e) => setConfig({ ...config, valorPadrao: e.target.value })}
            onBlur={() => void salvar({ valorPadrao: config.valorPadrao })}
            inputMode="decimal"
            prefixo="R$"
            dica="A inscrição em si é gratuita — este é o valor cobrado para participar do acampamento. Aplicado no momento do envio da ficha; mudar aqui não altera quem já se inscreveu."
          />

          <CampoArea
            rotulo="Local do acampamento"
            value={config.local}
            onChange={(e) => setConfig({ ...config, local: e.target.value })}
            onBlur={() => void salvar({ local: config.local })}
            opcional
            dica="Nome do sítio e endereço. Aparece na tela inicial, no começo da ficha e no e-mail de autorização."
          />

          <CampoArea
            rotulo="Data e horário"
            value={config.periodo}
            onChange={(e) => setConfig({ ...config, periodo: e.target.value })}
            onBlur={() => void salvar({ periodo: config.periodo })}
            opcional
            dica='Texto livre — ex.: "02 a 05 de abril de 2026, saída da igreja às 23h do dia 02 e retorno às 18h do dia 05". Aparece no e-mail de autorização.'
          />

          <CampoArea
            rotulo="Condições de pagamento"
            value={config.instrucoesPagamento}
            onChange={(e) => setConfig({ ...config, instrucoesPagamento: e.target.value })}
            onBlur={() => void salvar({ instrucoesPagamento: config.instrucoesPagamento })}
            opcional
            maximo={1000}
            dica="Onde e como pagar, prazos e formas aceitas. Aparece na revisão da ficha, logo acima da confirmação de ciência."
          />
        </div>
      </Cartao>

      <Cartao titulo="Avisos automáticos">
        <div className="pilha">
          <p className="campo__dica">
            Cada regra liga e desliga separadamente. Os envios saem em fila escalonada para não
            estourar a cota diária de e-mails.
          </p>

          {(Object.keys(ROTULO_REGRA) as (keyof RegrasEnvio)[]).map((chave) => (
            <div key={chave} className="escolha" style={{ cursor: 'default' }}>
              <Alternador
                ligado={config.regrasEnvio[chave]}
                desabilitado={salvando}
                aoMudar={(v) =>
                  void salvar({ regrasEnvio: { ...config.regrasEnvio, [chave]: v } })
                }
                titulo={ROTULO_REGRA[chave].titulo}
                descricao={ROTULO_REGRA[chave].texto}
              />
            </div>
          ))}
        </div>
      </Cartao>

      <TrocarSenha />

      <FilaEmails />

      <Cartao titulo="Disparo manual">
        <div className="pilha">
          <p className="campo__dica">
            Envia agora para todos os elegíveis, respeitando a cota diária.
          </p>

          <div className="linha">
            {DISPAROS.map((d) => (
              <Botao
                key={d.template}
                variante="vazado"
                tamanho="pequeno"
                carregando={disparando === d.template}
                disabled={disparando !== null}
                onClick={() =>
                  perguntar({
                    titulo: d.rotulo,
                    texto:
                      'Isso enfileira e-mails para todos os elegíveis. O envio é escalonado ao longo dos dias necessários para não estourar a cota diária.',
                    confirmar: 'Enfileirar',
                    aoConfirmar: () => disparar(d.template),
                  })
                }
              >
                {d.rotulo}
              </Botao>
            ))}
          </div>
        </div>
      </Cartao>
    </div>
  );
}
